import React, { useEffect, useState } from 'react';
import { api } from '../services/api';

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
};

const OpsTaxExemptionRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [actionLoading, setActionLoading] = useState('');
  const [decisionNotes, setDecisionNotes] = useState({});
  const [pendingOnly, setPendingOnly] = useState(true);

  const loadRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/acct-mgmt/v1/support/tax-exemption-requests');
      setRequests(response.data?.requests || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load tax exemption requests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const visibleRequests = pendingOnly
    ? requests.filter((request) => request.status === 'pending_review')
    : requests;

  const handleDecision = async (requestId, decision) => {
    const actionKey = `${decision}-${requestId}`;
    setActionLoading(actionKey);
    setStatus({ type: '', message: '' });
    setError('');
    try {
      const response = await api.post(`/acct-mgmt/v1/support/tax-exemption-requests/${requestId}/decision`, {
        decision,
        decision_notes: decisionNotes[requestId] || '',
      });
      setStatus({
        type: 'success',
        message: response.data?.message || `Request ${decision === 'approve' ? 'approved' : 'rejected'}.`,
      });
      await loadRequests();
      window.dispatchEvent(new Event('finbase:tax-exemption-pending-count-refresh'));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update tax exemption request.');
    } finally {
      setActionLoading('');
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 className="h4 fw-bold mb-1">Tax Exemption Requests</h2>
          <div className="text-muted">Review and decision tax exemption requests submitted by customer organizations.</div>
        </div>
        <div className="d-flex align-items-center gap-3">
          <div className="form-check form-switch finbase-toggle m-0">
            <input
              className="form-check-input"
              type="checkbox"
              id="pendingReviewOnly"
              checked={pendingOnly}
              onChange={(event) => setPendingOnly(event.target.checked)}
            />
            <label className="form-check-label" htmlFor="pendingReviewOnly">
              Pending Review Only
            </label>
          </div>
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={loadRequests} disabled={loading || Boolean(actionLoading)}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {status.message && <div className={`alert alert-${status.type}`} role="alert">{status.message}</div>}
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      <div className="alert alert-info" role="alert">
        Supporting documentation for tax exemption requests is uploaded and reviewed through the support portal. Use the support ticket reference below to open the Zendesk agent ticket when available.
      </div>

      <div className="card">
        <div className="card-body">
          {loading ? (
            <div className="text-muted">Loading tax exemption requests...</div>
          ) : visibleRequests.length === 0 ? (
            <div className="text-muted">{pendingOnly ? 'No pending-review tax exemption requests found.' : 'No tax exemption requests found.'}</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Submitted</th>
                    <th>Organization</th>
                    <th>Jurisdiction</th>
                    <th>Exemption ID</th>
                    <th>Contact Email</th>
                    <th>Support Ticket</th>
                    <th>Reason</th>
                    <th>Decision Notes</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRequests.map((request) => {
                    const isPending = request.status === 'pending_review';
                    return (
                      <tr key={request.id}>
                        <td>{formatDateTime(request.created_at)}</td>
                        <td>
                          <div className="fw-semibold">{request.org_name}</div>
                          <div className="text-muted small">Org #{request.org_id}</div>
                        </td>
                        <td>{request.jurisdiction_state}</td>
                        <td>{request.exemption_id_value || '—'}</td>
                        <td>{request.contact_email || '—'}</td>
                        <td>
                          {request.support_ticket_id ? (
                            <div>
                              <div className="fw-semibold">#{request.support_ticket_id}</div>
                              {request.support_agent_ticket_url && (
                                <a
                                  href={request.support_agent_ticket_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="small"
                                >
                                  View in Zendesk
                                </a>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>
                        <td style={{ minWidth: 180 }}>{request.exemption_reason || '—'}</td>
                        <td style={{ minWidth: 220 }}>
                          {isPending ? (
                            <textarea
                              className="form-control form-control-sm"
                              rows="2"
                              value={decisionNotes[request.id] || ''}
                              onChange={(event) => setDecisionNotes((prev) => ({ ...prev, [request.id]: event.target.value }))}
                              placeholder="Optional decision notes"
                              disabled={Boolean(actionLoading)}
                            />
                          ) : (
                            <div className="small">{request.decision_notes || '—'}</div>
                          )}
                        </td>
                        <td className="text-capitalize">{String(request.status || '—').replace(/_/g, ' ')}</td>
                        <td>
                          {isPending ? (
                            <div className="d-flex flex-wrap gap-2">
                              <button
                                type="button"
                                className="btn btn-outline-success btn-sm"
                                onClick={() => handleDecision(request.id, 'approve')}
                                disabled={Boolean(actionLoading)}
                              >
                                {actionLoading === `approve-${request.id}` ? 'Approving...' : 'Approve'}
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleDecision(request.id, 'reject')}
                                disabled={Boolean(actionLoading)}
                              >
                                {actionLoading === `reject-${request.id}` ? 'Rejecting...' : 'Reject'}
                              </button>
                            </div>
                          ) : (
                            <div className="small text-muted">Decisioned {formatDateTime(request.reviewed_at)}</div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OpsTaxExemptionRequests;
